/**
 * Polyline → Three.js geometry conversion
 *
 * Converts polylines (Vec3[][]) into BufferGeometry:
 * - Line segments with per-polyline vertex colors
 * - Tube meshes with variable radius profiles
 */

import type { Three } from './types';
import type { StyledPolyline } from '@plugins/generative/TurtleNode/TurtleNode';

const DEFAULT_COLOR = '#3b82f6';

/**
 * Convert styled polylines to a THREE.BufferGeometry containing line segments.
 * Each polyline of n points produces (n-1) segments (pairs of vertices).
 * Includes per-polyline vertex colors.
 */
export function polylinesToGeometry(three: Three, polylines: StyledPolyline[]): any {
  let totalVertices = 0;
  for (const polyline of polylines) {
    if (polyline.points.length >= 2) {
      totalVertices += (polyline.points.length - 1) * 2;
    }
  }

  const positions = new Float32Array(totalVertices * 3);
  const colors = new Float32Array(totalVertices * 3);
  let offset = 0;
  let colorOffset = 0;

  const tmpColor = new three.Color();

  for (const polyline of polylines) {
    if (polyline.points.length < 2) continue;
    tmpColor.set(polyline.color ?? DEFAULT_COLOR);
    const r = tmpColor.r, g = tmpColor.g, b = tmpColor.b;

    for (let i = 0; i < polyline.points.length - 1; i++) {
      const a = polyline.points[i];
      const pt = polyline.points[i + 1];
      positions[offset++] = a.x;
      positions[offset++] = a.y;
      positions[offset++] = a.z;
      positions[offset++] = pt.x;
      positions[offset++] = pt.y;
      positions[offset++] = pt.z;
      // Both vertices of the segment get the polyline's color
      colors[colorOffset++] = r; colors[colorOffset++] = g; colors[colorOffset++] = b;
      colors[colorOffset++] = r; colors[colorOffset++] = g; colors[colorOffset++] = b;
    }
  }

  const geometry = new three.BufferGeometry();
  geometry.setAttribute('position', new three.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new three.BufferAttribute(colors, 3));
  return geometry;
}

/** Linearly interpolate the radiusProfile array at parameter t ∈ [0,1]. */
function sampleProfile(profile: number[], t: number): number {
  if (profile.length === 0) return 1;
  if (profile.length === 1) return profile[0];
  const idx = t * (profile.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.min(lo + 1, profile.length - 1);
  const frac = idx - lo;
  return profile[lo] * (1 - frac) + profile[hi] * frac;
}

/**
 * Add a disc cap at one end of a tube (triangle fan from center to ring vertices).
 * @param ringIndex - which ring (0 = start, tubularSegments = end)
 * @param isStart - true for start cap (winding flipped so normal faces outward)
 */
function addDiscCap(
  three: Three, curve: any, tubePos: any,
  ringIndex: number, ringSize: number,
  tubularSegments: number, radialSegments: number,
  _capRadius: number,
  cr: number, cg: number, cb: number,
  positions: number[], normals: number[], colors: number[], indices: number[],
  vertexOffset: number, isStart: boolean
): void {
  const t = ringIndex / tubularSegments;
  const center = curve.getPointAt(t);
  const tangent = curve.getTangentAt(t);
  // Cap normal faces outward: start cap faces opposite to tangent, end cap faces along tangent
  const nx = isStart ? -tangent.x : tangent.x;
  const ny = isStart ? -tangent.y : tangent.y;
  const nz = isStart ? -tangent.z : tangent.z;

  // Center vertex
  const centerIdx = vertexOffset;
  positions.push(center.x, center.y, center.z);
  normals.push(nx, ny, nz);
  colors.push(cr, cg, cb);

  // Ring vertices (read already-scaled positions from the tube geometry)
  const ringStart = ringIndex * ringSize;
  for (let s = 0; s < radialSegments; s++) {
    const vi = ringStart + s;
    positions.push(tubePos.getX(vi), tubePos.getY(vi), tubePos.getZ(vi));
    normals.push(nx, ny, nz);
    colors.push(cr, cg, cb);
  }

  // Triangle fan indices
  for (let s = 0; s < radialSegments; s++) {
    const curr = vertexOffset + 1 + s;
    const next = vertexOffset + 1 + ((s + 1) % radialSegments);
    if (isStart) {
      // Reverse winding for start cap
      indices.push(centerIdx, next, curr);
    } else {
      indices.push(centerIdx, curr, next);
    }
  }
}

export interface TubeOptions {
  radius: number;
  radiusProfile: number[];
  radialSegments: number;
}

/**
 * Convert styled polylines to merged tube geometry.
 * Uses TubeGeometry per polyline then post-scales rings by radiusProfile.
 * Returns { geometry, material } ready to create a Mesh.
 */
export function polylinesToTubes(
  three: Three,
  polylines: StyledPolyline[],
  opts: TubeOptions
): { geometry: any; material: any } {
  const { radius, radiusProfile, radialSegments } = opts;
  const allPositions: number[] = [];
  const allNormals: number[] = [];
  const allColors: number[] = [];
  const allIndices: number[] = [];
  let vertexOffset = 0;

  const tmpColor = new three.Color();

  for (const polyline of polylines) {
    if (polyline.points.length < 2) continue;

    const curvePoints = polyline.points.map(p => new three.Vector3(p.x, p.y, p.z));
    const curve = new three.CatmullRomCurve3(curvePoints, false, 'centripetal', 0.5);

    const tubularSegments = Math.max(polyline.points.length * 4, 16);
    const tubeGeo = new three.TubeGeometry(curve, tubularSegments, 1.0, radialSegments, false);

    // Scale each ring by radius * profile sample
    const posAttr = tubeGeo.getAttribute('position');
    const normAttr = tubeGeo.getAttribute('normal');
    const ringSize = radialSegments + 1;
    const numRings = tubularSegments + 1;

    for (let ring = 0; ring < numRings; ring++) {
      const t = ring / tubularSegments;
      const profileScale = radius * sampleProfile(radiusProfile, t);
      const center = curve.getPointAt(t);

      for (let seg = 0; seg < ringSize; seg++) {
        const vi = ring * ringSize + seg;
        const vx = posAttr.getX(vi);
        const vy = posAttr.getY(vi);
        const vz = posAttr.getZ(vi);
        // Vector from center to vertex (unit-radius tube), scale by desired radius
        const dx = vx - center.x;
        const dy = vy - center.y;
        const dz = vz - center.z;
        posAttr.setXYZ(vi, center.x + dx * profileScale, center.y + dy * profileScale, center.z + dz * profileScale);
      }
    }

    tubeGeo.computeVertexNormals();

    // Extract tube arrays and merge
    const pos = tubeGeo.getAttribute('position');
    const norm = tubeGeo.getAttribute('normal');
    const idx = tubeGeo.getIndex();

    tmpColor.set(polyline.color ?? DEFAULT_COLOR);
    const cr = tmpColor.r, cg = tmpColor.g, cb = tmpColor.b;

    for (let i = 0; i < pos.count; i++) {
      allPositions.push(pos.getX(i), pos.getY(i), pos.getZ(i));
      allNormals.push(norm.getX(i), norm.getY(i), norm.getZ(i));
      allColors.push(cr, cg, cb);
    }

    if (idx) {
      for (let i = 0; i < idx.count; i++) {
        allIndices.push(idx.getX(i) + vertexOffset);
      }
    }

    vertexOffset += pos.count;
    tubeGeo.dispose();

    // ── End caps (disc triangle fans) ──
    // Only add caps if the profile radius at that end is > 0
    const startProfileR = radius * sampleProfile(radiusProfile, 0);
    const endProfileR = radius * sampleProfile(radiusProfile, 1);

    if (startProfileR > 1e-6) {
      addDiscCap(three, curve, pos, 0, ringSize, tubularSegments, radialSegments,
        startProfileR, cr, cg, cb, allPositions, allNormals, allColors, allIndices, vertexOffset, true);
      vertexOffset += radialSegments + 1; // center + ring vertices
    }

    if (endProfileR > 1e-6) {
      addDiscCap(three, curve, pos, tubularSegments, ringSize, tubularSegments, radialSegments,
        endProfileR, cr, cg, cb, allPositions, allNormals, allColors, allIndices, vertexOffset, false);
      vertexOffset += radialSegments + 1;
    }
  }

  const geometry = new three.BufferGeometry();
  geometry.setAttribute('position', new three.BufferAttribute(new Float32Array(allPositions), 3));
  geometry.setAttribute('normal', new three.BufferAttribute(new Float32Array(allNormals), 3));
  geometry.setAttribute('color', new three.BufferAttribute(new Float32Array(allColors), 3));
  if (allIndices.length > 0) {
    geometry.setIndex(new three.BufferAttribute(new Uint32Array(allIndices), 1));
  }

  const material = new three.MeshStandardMaterial({ vertexColors: true });

  return { geometry, material };
}
